import React, { useContext, useEffect, useRef, useState } from 'react'
import DetailsBox, { DetailRow } from '../../../components/Cards/DetailsBox'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import moment from 'moment'
import CustomDataGrid from '../../../components/DataGrid/CustomDataGrid'
import Download from '@mui/icons-material/Download'
import API from '../../../services/API'
import BigModal from '../../../components/Modals/BigModal'
import { GlobalContext, UserContext } from '../../../App'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import SmallModal from '../../../components/Modals/SmallModal'

const DocumentPopup = ({
  clientID,
  document,
  onClose,
  handleDeleteDocument,
}) => {
  const [title, setTitle] = useState(document.title)
  const [viewableByClient, setViewableByClient] = useState(
    document.viewableByClient,
  )
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false)

  const handleSaveChanges = (e) => {
    e.preventDefault()
    API.post(`/event-planning/organisations/edit-document/`, {
      clientID,
      docID: document.id,
      viewableByClient,
      title,
    }).then((res) => res.status === 200 && window.location.reload())
  }

  return (
    <>
      <Stack
        gap={1}
        component="form"
        onSubmit={() => handleSubmit(document.id)}
      >
        <TextField
          fullWidth
          title="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
        />
        <DetailRow
          label="Date"
          value={moment(document.date).format('Do MMM YYYY')}
        />
        <DetailRow label="Author" value={document.author} />
        <FormControlLabel
          label="Viewable by Client"
          control={
            <Switch
              checked={viewableByClient}
              onChange={(e) => setViewableByClient(e.target.checked)}
              color="success"
            />
          }
        />
        <Button
          variant="outlined"
          fullWidth
          color="error"
          onClick={() => setOpenConfirmDelete(true)}
        >
          Delete Document
        </Button>
        <Button variant="contained" color="error" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleSaveChanges}
          disabled={
            document.title === title &&
            document.viewableByClient === viewableByClient
          }
        >
          Save Changes
        </Button>
      </Stack>
      <SmallModal
        open={openConfirmDelete}
        onClose={() => setOpenConfirmDelete(false)}
      >
        <Stack gap={1}>
          <Typography variant="title3" textAlign="center">
            Are you sure you want to delete this document?
          </Typography>
          <Button
            variant="contained"
            color="success"
            onClick={() => handleDeleteDocument(document.id, document.download)}
          >
            Yes - Delete
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => setOpenConfirmDelete(false)}
          >
            No - Cancel
          </Button>
        </Stack>
      </SmallModal>
    </>
  )
}

const Documents = ({ client }) => {
  const [allFiles, setAllFiles] = useState([])
  const [openUploadDocumentPopup, setOpenUploadDocumentPopup] = useState(false)
  const newFileInput = useRef()
  const [newDocTitle, setNewDocTitle] = useState('')
  const [newFile, setNewFile] = useState(null)
  const [newFileViewable, setNewFileViewable] = useState(false)
  const { setToast } = useContext(GlobalContext)
  const { user } = useContext(UserContext)
  const [selectedDocument, setSelectedDocument] = useState(null)

  const handleDeleteDocument = (docID, s3key) => {
    API.post(`/event-planning/organisations/delete-document`, {
      clientID: client._id,
      docID,
      s3key,
    }).then((res) => {
      if (res.status === 200) {
        setAllFiles((prev) =>
          prev
            .filter((x) => x.id !== docID)
            .sort((a, b) => moment(b.date) - moment(a.date)),
        )
        setToast({ message: 'Document Deleted' })
        setSelectedDocument(null)
      }
    })
  }

  const handleNewFileSubmission = (e) => {
    e.preventDefault()
    let s3key
    let s3url
    API.post('/gets3uploadUrl', {
      folder: 'clients/documents',
      extension: newFile.name.split('.').slice(-1)[0],
    }).then((res) => {
      s3url = res.data.url
      s3key = res.data.key
      API.put(s3url, newFile, {
        headers: {
          'Content-Type': 'application/pdf',
        },
      }).then((res) => {
        if (res.status === 200) {
          API.put(`/event-planning/organisations/${client._id}`, {
            $push: {
              documents: {
                createdAt: new Date(),
                title: newDocTitle,
                document: s3key,
                uploadedBy: user._id,
                viewableByClient: newFileViewable,
              },
            },
          }).then((res) => {
            if (res.status === 200) {
              setAllFiles((prev) => [
                {
                  date: new Date(),
                  title: newDocTitle,
                  download: s3key,
                  author: `${user.firstName} ${user.lastName}`,
                  id: res.data._id,
                },
                ...prev,
              ])
              setNewFile(null)
              setNewDocTitle('')
              setNewFileViewable(false)
              setOpenUploadDocumentPopup(false)
              setToast({ message: 'Document Uploaded' })
            }
          })
        }
      })
    })
  }

  const engagementLetters = client.engagementLetters
    ? client.engagementLetters.reduce(
        (accumulator, doc) => [
          ...accumulator,
          {
            date: doc.startDate,
            title: 'Engagement Letter',
            author: `${doc.author?.firstName} ${doc.author?.lastName}`,
            download: doc.document,
            id: doc._id,
            viewableByClient: doc.viewableByClient,
            isEngagementLetter: true,
          },
        ],
        [],
      )
    : []

  const documents = client.documents
    ? client.documents.reduce(
        (accumulator, doc) => [
          ...accumulator,
          {
            date: doc.createdAt,
            title: doc.title,
            author: `${doc.uploadedBy?.firstName} ${doc.uploadedBy?.lastName}`,
            download: doc.document,
            id: doc._id,
            viewableByClient: doc.viewableByClient,
          },
        ],
        [],
      )
    : []

  useEffect(
    () =>
      setAllFiles(
        [...engagementLetters, ...documents].sort(
          (a, b) => moment(b.date) - moment(a.date),
        ),
      ),
    [client],
  )

  return (
    <DetailsBox
      title="Documents"
      buttonRight={
        <UploadFileIcon
          onClick={() => {
            setOpenUploadDocumentPopup(true)
          }}
          sx={{ cursor: 'pointer' }}
          color="contrast"
        />
      }
    >
      <CustomDataGrid
        rows={allFiles}
        hideToolbar
        getRowId={(params) => params.id}
        disableRowSelectionOnClick
        onRowClick={({ row }) => setSelectedDocument(row)}
        columns={[
          {
            field: 'date',
            headerName: 'Date',
            minWidth: 200,
            renderCell: ({ row }) =>
              row.date && moment(row.date).format('Do MMM YYYY  HH:mm'),
          },
          { field: 'title', headerName: 'Title', minWidth: 250, flex: 1 },
          {
            field: 'author',
            headerName: 'Created By',
            minWidth: 250,
            flex: 0.5,
          },
          {
            field: 'viewableByClient',
            headerName: 'Viewable',
            headerAlign: 'center',
            description: 'Document viewable by client on the client portal',
            renderCell: ({ value }) => (
              <Box display="flex" width="100%" justifyContent="center">
                {value ? (
                  <VisibilityIcon color="success" />
                ) : (
                  <VisibilityOffIcon color="error" />
                )}
              </Box>
            ),
          },
          {
            field: 'download',
            headerName: 'Download',
            headerAlign: 'center',
            minWidth: 150,
            renderCell: ({ row }) => (
              <Box display="flex" width="100%" justifyContent="center">
                <Download
                  color="primary"
                  onClick={(event) => {
                    event.stopPropagation()
                    API.post('/gets3downloadurl?inlineView=true', {
                      key: row.download,
                    }).then((res) => {
                      window.open(res.data, '_blank')
                    })
                  }}
                />
              </Box>
            ),
          },
        ]}
        localeText={{ noRowsLabel: 'No Documents' }}
      />
      <BigModal
        autoWidth
        open={openUploadDocumentPopup}
        onClose={() => setOpenUploadDocumentPopup(false)}
      >
        <Stack gap={1} component="form" onSubmit={handleNewFileSubmission}>
          <Typography variant="title3">
            Add a Document to {client.orgName}
          </Typography>
          <TextField
            value={newDocTitle}
            onChange={(e) => setNewDocTitle(e.target.value)}
            size="small"
            label="Document Title"
            required
          />
          <Button
            variant="outlined"
            color="anMainBlue"
            onClick={() => newFileInput.current.click()}
            sx={{ display: newFile && 'none' }}
          >
            Select File
          </Button>
          {newFile && <Typography variant="caption">{newFile.name}</Typography>}
          <input
            ref={newFileInput}
            type="file"
            style={{ display: 'none' }}
            accept={'application/pdf'}
            onChange={(e) => setNewFile(e.target.files[0])}
            required
          />
          <FormControlLabel
            label="Viewable by Client"
            control={
              <Switch
                checked={newFileViewable}
                onChange={(e) => setNewFileViewable(e.target.checked)}
                color="success"
              />
            }
          />
          <ButtonGroup fullWidth>
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={() => {
                setNewFile(null)
                setNewDocTitle('')
                setOpenUploadDocumentPopup(false)
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="success"
              size="small"
              type="submit"
            >
              Submit
            </Button>
          </ButtonGroup>
        </Stack>
      </BigModal>
      <BigModal
        open={!!selectedDocument}
        onClose={() => setSelectedDocument(null)}
      >
        <DocumentPopup
          document={selectedDocument}
          onClose={() => setSelectedDocument(null)}
          handleDeleteDocument={handleDeleteDocument}
          clientID={client._id}
        />
      </BigModal>
    </DetailsBox>
  )
}

export default Documents
